import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DateRangeFilterPopover, DateRangeFilters } from './DateRangeFilterPopover';
import { SharingFilterPopover } from './SharingFilterPopover';

export interface DocumentFilterBarProps {
  visibility: string;
  sortBy: string;
  dateFilters: DateRangeFilters;
  sharedWithGroup: string;
  sharedWithUser: string;
  onVisibilityChange: (value: string) => void;
  onSortByChange: (value: string) => void;
  onDateFiltersChange: (filters: DateRangeFilters) => void;
  onSharedWithGroupChange: (groupId: string) => void;
  onSharedWithUserChange: (userId: string) => void;
  onClearFilters: () => void;
}

/**
 * DocumentFilterBar - Filter and sort controls for documents
 *
 * Provides dropdown filters for:
 * - Visibility (All, Public, Private, Organization, Group)
 * - Sort Order (Newest, Oldest, Recently Updated, A-Z, Z-A)
 * - Date Range (Created/Updated dates)
 *
 * Includes a "Clear Filters" button to reset all filters to defaults.
 */
export const DocumentFilterBar: React.FC<DocumentFilterBarProps> = ({
  visibility,
  sortBy,
  dateFilters,
  sharedWithGroup,
  sharedWithUser,
  onVisibilityChange,
  onSortByChange,
  onDateFiltersChange,
  onSharedWithGroupChange,
  onSharedWithUserChange,
  onClearFilters
}) => {
  const { t } = useTranslation();

  const handleVisibilityChange = (event: SelectChangeEvent) => {
    onVisibilityChange(event.target.value);
  };

  const handleSortByChange = (event: SelectChangeEvent) => {
    onSortByChange(event.target.value);
  };

  const hasActiveDateFilters = Boolean(
    dateFilters.createdFrom ||
    dateFilters.createdTo ||
    dateFilters.updatedFrom ||
    dateFilters.updatedTo
  );

  const hasActiveSharingFilters = Boolean(sharedWithGroup || sharedWithUser);

  const hasActiveFilters = visibility !== 'all' || sortBy !== 'newest' || hasActiveDateFilters || hasActiveSharingFilters;

  return (
    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon sx={{ color: 'text.secondary' }} />
      </Box>

      {/* Visibility Filter */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="visibility-filter-label">{t('documents.filters.visibility')}</InputLabel>
        <Select
          labelId="visibility-filter-label"
          id="visibility-filter"
          value={visibility}
          label={t('documents.filters.visibility')}
          onChange={handleVisibilityChange}
        >
          <MenuItem value="all">{t('documents.filters.all')}</MenuItem>
          <MenuItem value="public">{t('documents.filters.public')}</MenuItem>
          <MenuItem value="private">{t('documents.filters.private')}</MenuItem>
          <MenuItem value="organization">{t('documents.filters.organization')}</MenuItem>
          <MenuItem value="group">{t('documents.filters.group')}</MenuItem>
        </Select>
      </FormControl>

      {/* Sort Order */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="sort-by-label">{t('documents.filters.sortBy')}</InputLabel>
        <Select
          labelId="sort-by-label"
          id="sort-by"
          value={sortBy}
          label={t('documents.filters.sortBy')}
          onChange={handleSortByChange}
        >
          <MenuItem value="newest">{t('documents.filters.newest')}</MenuItem>
          <MenuItem value="oldest">{t('documents.filters.oldest')}</MenuItem>
          <MenuItem value="recently_updated">{t('documents.filters.recentlyUpdated')}</MenuItem>
          <MenuItem value="title_asc">{t('documents.filters.titleAsc')}</MenuItem>
          <MenuItem value="title_desc">{t('documents.filters.titleDesc')}</MenuItem>
        </Select>
      </FormControl>

      {/* Date Range Filter */}
      <DateRangeFilterPopover
        dateFilters={dateFilters}
        onDateFiltersChange={onDateFiltersChange}
      />

      {/* Sharing Filter */}
      <SharingFilterPopover
        selectedGroupId={sharedWithGroup}
        selectedUserId={sharedWithUser}
        onGroupChange={onSharedWithGroupChange}
        onUserChange={onSharedWithUserChange}
      />

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ClearIcon />}
          onClick={onClearFilters}
          sx={{ ml: 'auto' }}
        >
          {t('documents.filters.clearFilters')}
        </Button>
      )}
    </Box>
  );
};
